import { useState, useCallback, useEffect } from 'react'
import { Button } from 'react-bootstrap'

import OTPInputField from '../../../elements/v2/OTPInputField'
import ErrorToast from '../../../elements/v2/ErrorToast'
import { useSession } from '../../../../contexts/SessionContext'
import { useDirectDebitStatus } from '../../../../contexts/session/DirectDebitContext'

import { formatPhoneNumber } from '../../../../utils/utils'
import { completeSessionOtpVerification } from '../../../../services/SessionService'
import { ReactComponent as CheckCircleSvg } from '../../../../assets/svg/check-circle.svg'
import { ReactComponent as ArrowRightSvg } from '../../../../assets/svg/arrow-right.svg'

const OTP_LENGTH = 6
const RESEND_COOLDOWN_SECS = 90

const OTPVerificationScreen = ({
  otp,
  setOtp,
  otpError,
  setOtpError,
  otpChannel,
  setOtpChannel,
  sessionKey,
  otpSessionId,
  setOtpSessionId,
  showEmailVerification,
  userEmail,
  userPhone,
  sessionReference,
  showAccountDetails,
}) => {
  const { startOtpVerification } = useSession()
  const { otpRequestedAt, setOtpRequestedAt } = useDirectDebitStatus()
  const [loading, setLoading] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [success, setSuccess] = useState(false)
  const [secondsLeft, setSecondsLeft] = useState(RESEND_COOLDOWN_SECS)

  const formattedUserPhone = formatPhoneNumber(userPhone)
  const isPhoneChannel = otpChannel === 'sms'
  const maskedDestination = isPhoneChannel
    ? `*****${userPhone.slice(-4)}`
    : userEmail

  useEffect(() => {
    if (!otpRequestedAt) return
    const tick = () => {
      const elapsed = Math.floor((Date.now() - new Date(otpRequestedAt).getTime()) / 1000)
      setSecondsLeft(Math.max(0, RESEND_COOLDOWN_SECS - elapsed))
    }
    tick()
    const interval = setInterval(tick, 1000)
    return () => clearInterval(interval)
  }, [otpRequestedAt])

  const toggleOtpChannel = () => {
    setOtpChannel(isPhoneChannel ? 'email' : 'sms')
    showEmailVerification()
  }

  const handleOtpSubmit = useCallback(async () => {
    if (!otp || otp.length !== OTP_LENGTH) {
      setOtpError(`Please enter a valid ${OTP_LENGTH}-digit OTP`)
      setHasError(true)
      return
    }

    if (!sessionKey) {
      console.error('Session not properly initialized', sessionKey)
      setOtpError('Session not properly initialized')
      setHasError(true)
      return
    }

    setLoading(true)
    setOtpError('')
    setHasError(false)

    try {
      const response = await completeSessionOtpVerification({
        sessionKey,
        otpCode: otp,
      })

      if (response.data?.status === 'VERIFIED') {
        setSuccess(true)
        setTimeout(() => {
          showAccountDetails()
        }, 1000)
      } else {
        setOtp('')
        setOtpError(response.statusMessage || 'OTP verification failed')
        setHasError(true)
        setSuccess(false)
      }
    } catch (error) {
      console.error('Error completing OTP verification:', error)
      setOtp('')
      setOtpError(error.statusMessage || 'An error occurred during OTP verification')
      setHasError(true)
    } finally {
      setLoading(false)
    }
  }, [otp, sessionKey, showAccountDetails, setOtpError])

  const handleResendOtp = useCallback(async () => {
    if (secondsLeft > 0) return

    setLoading(true)
    setOtpError('')

    try {
      const response = await startOtpVerification(sessionKey, {
        verificationType: otpChannel.toUpperCase(),
        email: userEmail,
        phoneNumber: userPhone,
        channel: otpChannel,
        reference: sessionReference,
      })

      if (response.statusCode === '0') {
        const otpSession = response.data.otpSessions?.[0]
        if (otpSession) {
          setOtpSessionId(otpSession.otpSessionId)
        }
        setOtpRequestedAt(new Date())
      } else {
        setOtpError(response.statusMessage || 'Failed to resend OTP')
      }
    } catch (error) {
      console.error('Error resending OTP:', error)
      setOtpError(error.message || 'An error occurred while resending OTP')
    } finally {
      setLoading(false)
    }
  }, [sessionKey, userEmail, userPhone, sessionReference, otpChannel, secondsLeft, setOtpError])

  return (
    <div
      style={{ height: '100%', padding: 'var(--dd-content-padding-y) var(--dd-content-padding-x)' }}
      className='w-100 d-flex flex-column justify-content-between'
    >
      <div>
      {success && (
        <div
          className='rounded d-flex align-items-center gap-2 mb-3'
          style={{ background: '#EBF6EB', padding: '12px 8px' }}
        >
          <CheckCircleSvg width={16} height={16} />
          <p className='small fw-medium mb-0' style={{ color: '#376E37' }}>
            {isPhoneChannel ? 'Phone number validation successful' : 'Email validation successful'}
          </p>
        </div>
      )}

      <div
        className='mb-3'
        style={{
          background: '#F7F7F7',
          border: '0.5px solid #EAECF0',
          boxShadow: '0px 1px 1px 0px #0000000D',
          borderRadius: '12px',
          padding: '24px 0',
        }}
      >
        <div className='px-4 d-flex justify-content-center align-items-center'>
          <div>
            <div className='mb-4 mx-auto text-center'>
              <h6 className='fw-semibold mb-1'>
                Let&apos;s make sure it&apos;s you
              </h6>
              <p className='small d-block w-100'>
                Enter the OTP sent to{' '}
                <span className='fw-medium'>{maskedDestination}</span>
                <br />
                to use your linked details
              </p>
            </div>

            <OTPInputField
              value={otp}
              onChange={(value) => {
                setOtp(value)
                if (otpError) {
                  setOtpError('')
                  setHasError(false)
                }
                if (success) {
                  setSuccess(false)
                }
              }}
              noOfInputs={OTP_LENGTH}
              isSecure={true}
              hasError={hasError}
              success={success}
              inputSize={38}
            />

            <ErrorToast message={otpError} className='mt-2' />
          </div>
        </div>

        <div
          className='d-flex align-items-center justify-content-center mt-3'
          style={{
            background: '#FFFFFF',
            border: '1px solid #F2F2F2',
            padding: '14px 0',
          }}
        >
          {secondsLeft > 0 ? (
            <p className='small fw-medium mb-0' style={{ color: '#939598' }}>
              Resend OTP in
              <span style={{ color: '#F2931E', marginLeft: '4px' }}>
                {Math.floor(secondsLeft / 60)}:{String(secondsLeft % 60).padStart(2, '0')}s
              </span>
            </p>
          ) : (
            <button
              type='button'
              className='btn btn-link small fw-medium p-0 text-decoration-none'
              style={{ color: '#027A48' }}
              onClick={handleResendOtp}
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Resend OTP'}
            </button>
          )}
        </div>
      </div>

      <div className='d-flex align-items-center justify-content-between mb-3'>
        <hr className='flex-grow-1' style={{ borderColor: '#EAECF0' }} />
        <p className='small fw-medium mb-0 mx-2 text-nowrap' style={{ color: '#939598' }}>
          Issues with your {isPhoneChannel ? 'phone number' : 'email'}?
        </p>
        <hr className='flex-grow-1' style={{ borderColor: '#EAECF0' }} />
      </div>

      <button
        type='button'
        onClick={toggleOtpChannel}
        className='bg-white d-flex align-items-center justify-content-between w-100 rounded-3'
        style={{
          border: '0.5px solid #D9D9D9',
          boxShadow: '0px 1px 0.5px 0px #00000008',
          padding: '16px 12px',
        }}
      >
        <span className='small fw-medium' style={{ color: '#414141' }}>
          {isPhoneChannel ? 'Use email address instead' : 'Use phone number instead'}
        </span>
        <ArrowRightSvg width={20} height={20} />
      </button>
      </div>

      <Button
        style={{
          background: otp.length === OTP_LENGTH ? '#F26522' : '#FCE9D2',
          padding: '12px',
          marginTop: '24px',
        }}
        className='w-100 rounded-pill text-white border-0'
        onClick={handleOtpSubmit}
        disabled={loading || otp.length !== OTP_LENGTH}
      >
        {loading ? 'Verifying...' : 'Proceed'}
      </Button>
    </div>
  )
}

export default OTPVerificationScreen
